import React, { useState, useEffect, useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import api from '../../api';

const UserManager = ({ configId }) => {
  const { showNotification } = useContext(NotificationContext);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    phone_number: '',
    email: '',
    role_id: ''
  });
  const [provisionFormData, setProvisionFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    phone_number: '',
    email: '',
    role_id: '',
    password: '',
    confirm_password: '',
    looker_embed_url: '',
    onboarding_link: ''
  });

  useEffect(() => {
    loadUsers();
    loadUserRoles();
  }, [configId]);

  const loadUsers = async () => {
    try {
      const response = await api.get(`/admin/users/${configId}`);
      setUsers(response.data);
    } catch (error) {
      showNotification('Error loading users: ' + error.message, true);
    }
  };

  const loadUserRoles = async () => {
    try {
      const response = await api.get('/enum/user-role/');
      setUserRoles(response.data);
    } catch (error) {
      showNotification('Error loading user roles: ' + error.message, true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        configuration_id: parseInt(configId),
        first_name: formData.first_name,
        last_name: formData.last_name,
        display_name: formData.display_name || undefined,
        phone_number: formData.phone_number || undefined,
        email: formData.email || undefined,
        role_id: formData.role_id ? parseInt(formData.role_id) : undefined
      };

      await api.post('/admin/users/', submissionData);
      showNotification('User added successfully!');
      setIsModalOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        display_name: '',
        phone_number: '',
        email: '',
        role_id: ''
      });
      loadUsers();
    } catch (error) {
      showNotification('Failed to add user: ' + error.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleProvisionSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (provisionFormData.password !== provisionFormData.confirm_password) {
      showNotification('Passwords do not match!', true);
      return;
    }

    // Validate password length
    if (provisionFormData.password.length < 6) {
      showNotification('Password must be at least 6 characters long!', true);
      return;
    }

    setProvisionLoading(true);

    try {
      const submissionData = {
        configuration_id: parseInt(configId),
        first_name: provisionFormData.first_name,
        last_name: provisionFormData.last_name,
        display_name: provisionFormData.display_name || undefined,
        phone_number: provisionFormData.phone_number || undefined,
        email: provisionFormData.email,
        role_id: parseInt(provisionFormData.role_id),
        password: provisionFormData.password,
        looker_embed_url: provisionFormData.looker_embed_url || undefined,
        onboarding_link: provisionFormData.onboarding_link || undefined
      };

      const response = await api.post('/admin/user-provisioning/provision-user', submissionData);
      
      if (response.data.success) {
        showNotification(`User provisioned successfully! UID: ${response.data.uid}. Verification email sent.`);
        setIsProvisionModalOpen(false);
        setProvisionFormData({
          first_name: '',
          last_name: '',
          display_name: '',
          phone_number: '',
          email: '',
          role_id: '',
          password: '',
          confirm_password: '',
          looker_embed_url: '',
          onboarding_link: ''
        });
        loadUsers();
      } else {
        showNotification('Failed to provision user: ' + response.data.message, true);
      }
    } catch (error) {
      showNotification('Failed to provision user: ' + error.message, true);
    } finally {
      setProvisionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/admin/users/${id}`);
      showNotification('User deleted successfully!');
      loadUsers();
    } catch (error) {
      showNotification('Failed to delete user: ' + error.message, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProvisionInputChange = (e) => {
    const { name, value } = e.target;
    setProvisionFormData(prev => ({ ...prev, [name]: value }));
  };

  const getRoleName = (roleId) => {
    const role = userRoles.find(r => r.id === roleId);
    return role ? role.name : '-';
  };

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>Users</h2>
        <div className="button-group">
          <button 
            className="add-entity-btn secondary"
            onClick={() => setIsModalOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
            </svg>
            <span>Add User (SQL Only)</span>
          </button>
          <button 
            className="add-entity-btn primary"
            onClick={() => setIsProvisionModalOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
            </svg>
            <span>Provision User (Full)</span>
          </button>
        </div>
      </div>
      
      <div className="section-content">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Display Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Role</th>
                <th>UID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id}>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.display_name || '-'}</td>
                    <td>{user.phone_number || '-'}</td>
                    <td>{user.email || '-'}</td>
                    <td>{user.role?.name || getRoleName(user.role_id)}</td>
                    <td>{user.uid || '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Regular User Modal */}
      {isModalOpen && (
        <div className="section-modal">
          <button 
            className="modal-close"
            onClick={() => setIsModalOpen(false)}
          >
            ×
          </button>
          <h3>Add User (SQL Only)</h3>
          <p className="text-sm text-gray-600 mb-4">
            This will only add the user to the SQL database without Firebase authentication.
          </p>
          <form onSubmit={handleSubmit}>
            <div>
              <label className="form-label">First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Display Name</label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Optional display name"
              />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Role</label>
              <select
                name="role_id"
                value={formData.role_id}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select a role</option>
                {userRoles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User Provisioning Modal */}
      {isProvisionModalOpen && (
        <div className="section-modal">
          <button 
            className="modal-close"
            onClick={() => setIsProvisionModalOpen(false)}
          >
            ×
          </button>
          <h3>Provision User (Full)</h3>
          <p className="text-sm text-gray-600 mb-4">
            This will create the user in Firebase Authentication, Firestore, and SQL database with full provisioning.
          </p>
          <form onSubmit={handleProvisionSubmit}>
            <div>
              <label className="form-label">First Name *</label>
              <input
                type="text"
                name="first_name"
                value={provisionFormData.first_name}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={provisionFormData.last_name}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Display Name</label>
              <input
                type="text"
                name="display_name"
                value={provisionFormData.display_name}
                onChange={handleProvisionInputChange}
                className="form-input"
                placeholder="Optional display name"
              />
            </div>
            <div>
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                name="phone_number"
                value={provisionFormData.phone_number}
                onChange={handleProvisionInputChange}
                className="form-input"
                placeholder="+15551234567"
              />
            </div>
            <div>
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                value={provisionFormData.email}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Role *</label>
              <select
                name="role_id"
                value={provisionFormData.role_id}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
              >
                <option value="">Select a role</option>
                {userRoles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Password *</label>
              <input
                type="password"
                name="password"
                value={provisionFormData.password}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
                minLength="6"
              />
              <small className="text-gray-500">Minimum 6 characters</small>
            </div>
            <div>
              <label className="form-label">Confirm Password *</label>
              <input
                type="password"
                name="confirm_password"
                value={provisionFormData.confirm_password}
                onChange={handleProvisionInputChange}
                className="form-input"
                required
                minLength="6"
              />
            </div>
            <div>
              <label className="form-label">Looker Embed URL</label>
              <input
                type="url"
                name="looker_embed_url"
                value={provisionFormData.looker_embed_url}
                onChange={handleProvisionInputChange}
                className="form-input"
                placeholder="https://looker..."
              />
            </div>
            <div>
              <label className="form-label">Onboarding Link</label>
              <input
                type="url"
                name="onboarding_link"
                value={provisionFormData.onboarding_link}
                onChange={handleProvisionInputChange}
                className="form-input"
                placeholder="https://..."
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setIsProvisionModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={provisionLoading}>
                {provisionLoading ? 'Provisioning...' : 'Provision User'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UserManager; 